import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Chip, Menu, MenuItem, Button,
  CircularProgress, Collapse, Divider, Tooltip, Dialog,
  DialogTitle, DialogContent, IconButton, List, ListItem, ListItemButton, ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import { getTenableStatus, syncVulns, syncAssets, getVulns, getVulnSummary, markVulnPatched } from '../api/tenable';
import { uploadScanFile, importScanFile, listSampleFiles } from '../api/scans';

const SEV_COLOR = { critical: '#f44336', high: '#ff9800', medium: '#ffeb3b', low: '#4caf50', info: '#607d8b' };
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' };

// ── Stat Box ───────────────────────────────────────────────────────────────────

function StatBox({ label, value, color, active, onClick }) {
  return (
    <Paper onClick={onClick} sx={{
      flex: 1, backgroundColor: active ? `${color}11` : '#111827',
      border: `1px solid ${active ? color : '#1e3a5f'}`,
      borderRadius: 2, padding: '16px 20px', textAlign: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s',
      '&:hover': onClick ? { border: `1px solid ${color}`, backgroundColor: `${color}11` } : {},
    }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

// ── Filter Chip ────────────────────────────────────────────────────────────────

function FilterChip({ label, active, color, onClick }) {
  return (
    <Chip label={label} size="small" onClick={onClick} sx={{
      backgroundColor: active ? (color ? `${color}33` : '#0d2137') : '#0a1525',
      color:           active ? (color || '#00bcd4') : '#7a8fa6',
      border:          `1px solid ${active ? (color || '#00bcd4') : '#1e3a5f'}`,
      fontWeight:      active ? 700 : 400,
      cursor:          'pointer', fontSize: 12,
    }} />
  );
}

// ── Col Filter ─────────────────────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }}
                sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// ── Vuln Row ───────────────────────────────────────────────────────────────────

function VulnRow({ v, onPatch }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper sx={{
      backgroundColor: '#111827',
      border: '1px solid #1e3a5f',
      borderLeft: `3px solid ${SEV_COLOR[v.severity] || '#607d8b'}`,
      borderRadius: 1,
      '&:hover': { backgroundColor: '#0d1f33' },
    }}>
      {/* Main row */}
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 110px 80px 80px 32px',
          alignItems: 'center',
          padding: '10px 14px',
          gap: 1,
          cursor: 'pointer',
        }}
      >
        <Box>
          <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{v.plugin_id}</Typography>
          {v.cve_id && <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 10 }}>{v.cve_id}</Typography>}
        </Box>
        <Box>
          <Typography sx={{ color: '#c0cfe0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</Typography>
          {v.asset_hostname && (
            <Typography sx={{ color: '#4a5a70', fontSize: 10, fontFamily: 'monospace' }}>{v.asset_hostname}</Typography>
          )}
        </Box>
        <Chip label={SEV_LABEL[v.severity] || v.severity} size="small" sx={{
          backgroundColor: `${SEV_COLOR[v.severity] || '#607d8b'}22`,
          color: SEV_COLOR[v.severity] || '#607d8b',
          fontWeight: 700, fontSize: 11, height: 20, width: 'fit-content',
        }} />
        <Typography sx={{ color: v.cvss_score >= 9 ? '#f44336' : v.cvss_score >= 7 ? '#ff9800' : '#c0cfe0', fontWeight: 700, fontSize: 13 }}>
          {v.cvss_score ? v.cvss_score.toFixed(1) : '—'}
        </Typography>
        <Chip
          label={v.patched ? 'Patched' : 'Open'}
          size="small"
          onClick={(e) => { e.stopPropagation(); if (!v.patched) onPatch(v.id); }}
          sx={{
            backgroundColor: v.patched ? '#0a2a1a' : '#2a0a0a',
            color: v.patched ? '#4caf50' : '#f44336',
            fontWeight: 700, fontSize: 10, height: 18,
            cursor: v.patched ? 'default' : 'pointer',
          }}
        />
        <Box sx={{ color: '#4a5a70', display: 'flex', alignItems: 'center' }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: '#1e3a5f' }} />
        <Box sx={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 1.5, backgroundColor: '#0a0e1a' }}>
          {v.description && (
            <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Description</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.description}</Typography>
            </Box>
          )}
          {v.solution && (
            <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Solution</Typography>
              <Typography sx={{ color: '#4caf50', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.solution}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {v.first_seen && <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>First Seen</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 11 }}>{new Date(v.first_seen).toLocaleDateString()}</Typography>
            </Box>}
            {v.last_seen && <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Seen</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 11 }}>{new Date(v.last_seen).toLocaleDateString()}</Typography>
            </Box>}
            {v.patch_available && <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Patch</Typography>
              <Typography sx={{ color: '#4caf50', fontSize: 11 }}>Available</Typography>
            </Box>}
            <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Source</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 11, fontFamily: 'monospace' }}>{v.source || 'tenable'}</Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Import Dialog ──────────────────────────────────────────────────────────────

function ImportDialog({ open, onClose, onImported }) {
  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState('');
  const fileRef               = useRef();

  useEffect(() => {
    if (open) {
      setStatus('');
      listSampleFiles().then(r => setFiles(r.files || [])).catch(() => setFiles([]));
    }
  }, [open]);

  const handleImport = async (filename) => {
    setLoading(true);
    setStatus('');
    try {
      const r = await importScanFile(filename);
      setStatus(`✓ ${r.scan_type} scan imported — ${r.hosts_in_file} hosts, ${r.vulns_created ?? r.assets_created ?? r.checks_created ?? 0} records created`);
      onImported();
    } catch (e) {
      setStatus('Import failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setStatus('');
    try {
      const r = await uploadScanFile(file);
      setStatus(`✓ ${r.scan_type} scan uploaded — ${r.hosts_in_file} hosts, ${r.vulns_created ?? r.assets_created ?? r.checks_created ?? 0} records created`);
      onImported();
    } catch (e) {
      setStatus('Upload failed: ' + e.message);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Import Nessus Scan
        <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>

        {/* Manual upload */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Upload File</Typography>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={loading}
            sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 12, '&:hover': { backgroundColor: '#00bcd411' } }}>
            Choose .nessus file
            <input ref={fileRef} type="file" accept=".nessus" hidden onChange={handleUpload} />
          </Button>
        </Box>

        <Divider sx={{ borderColor: '#1e3a5f', my: 2 }} />

        {/* Sample files */}
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Sample Files (Server)</Typography>
        {files.length === 0 ? (
          <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>No sample files found on server.</Typography>
        ) : (
          <List dense disablePadding>
            {files.map(f => (
              <ListItem key={f} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton disabled={loading} onClick={() => handleImport(f)}
                  sx={{ backgroundColor: '#0d1825', border: '1px solid #1e3a5f', borderRadius: 1, '&:hover': { backgroundColor: '#0d2137', borderColor: '#00bcd4' } }}>
                  <ListItemText
                    primary={f}
                    primaryTypographyProps={{ sx: { color: '#c0cfe0', fontSize: 13, fontFamily: 'monospace' } }}
                    secondary={
                      f.includes('discovery') ? 'Host discovery · assets only' :
                      f.includes('vuln') ? 'Vulnerability scan · CVEs + CVSS scores' :
                      f.includes('config') ? 'Configuration scan · STIG compliance checks' : ''
                    }
                    secondaryTypographyProps={{ sx: { color: '#4a5a70', fontSize: 11 } }}
                  />
                  {loading ? <CircularProgress size={14} sx={{ color: '#00bcd4' }} /> : null}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {status && (
          <Typography sx={{ color: status.startsWith('✓') ? '#4caf50' : '#f44336', fontSize: 12, mt: 2 }}>
            {status}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function Vulnerabilities() {
  const [tenableStatus, setTenableStatus] = useState(null);
  const [vulns, setVulns]                 = useState([]);
  const [summary, setSummary]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [syncing, setSyncing]             = useState(false);
  const [syncMsg, setSyncMsg]             = useState('');
  const [importOpen, setImportOpen]       = useState(false);

  const [filterSev, setFilterSev]         = useState('all');
  const [filterPatched, setFilterPatched] = useState('open');
  const [filterSevCol, setFilterSevCol]   = useState([]);

  const loadData = () => {
    setLoading(true);
    Promise.all([getTenableStatus(), getVulnSummary(), getVulns({ limit: 500 })])
      .then(([status, sum, vulnData]) => {
        setTenableStatus(status);
        setSummary(sum);
        setVulns(vulnData.vulnerabilities || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const assetResult = await syncAssets();
      const vulnResult  = await syncVulns();
      setSyncMsg(`Synced: ${assetResult.created + assetResult.updated} assets, ${vulnResult.created + vulnResult.updated} vulns`);
      loadData();
    } catch (e) {
      setSyncMsg('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePatch = async (vulnId) => {
    await markVulnPatched(vulnId);
    setVulns(prev => prev.map(v => v.id === vulnId ? { ...v, patched: true } : v));
  };

  const filtered = useMemo(() => vulns.filter(v => {
    if (filterSev !== 'all' && v.severity !== filterSev) return false;
    if (filterPatched === 'open' && v.patched) return false;
    if (filterPatched === 'patched' && !v.patched) return false;
    if (filterSevCol.length > 0 && !filterSevCol.includes(v.severity)) return false;
    return true;
  }), [vulns, filterSev, filterPatched, filterSevCol]);

  const configured = tenableStatus?.configured;
  const lastSync   = tenableStatus?.last_sync ? new Date(tenableStatus.last_sync).toLocaleString() : null;

  const statSevs = ['critical', 'high', 'medium', 'low'];

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2, padding: '8px 14px', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2 }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tenable</Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: configured ? (tenableStatus?.status === 'active' ? '#4caf50' : '#ff9800') : '#4a5a70' }} />
        <Typography sx={{ color: '#ffffff', fontSize: 12, fontWeight: 600 }}>
          {configured ? (tenableStatus?.mode === 'sc' ? 'Security Center' : 'Tenable.io') : 'Not Configured'}
        </Typography>
        {lastSync && <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>· Last sync: {lastSync}</Typography>}
        {syncMsg && <Typography sx={{ color: syncMsg.startsWith('Sync failed') ? '#f44336' : '#4caf50', fontSize: 11 }}>{syncMsg}</Typography>}
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <Button onClick={() => setImportOpen(true)} variant="outlined" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
            sx={{ borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 11, '&:hover': { borderColor: '#00bcd4', color: '#00bcd4', backgroundColor: '#00bcd411' } }}>
            Import Scan
          </Button>
          {configured && (
            <Button onClick={handleSync} disabled={syncing} variant="outlined" size="small" startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon sx={{ fontSize: 14 }} />}
              sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 11, '&:hover': { backgroundColor: '#00bcd411' } }}>
              {syncing ? 'Syncing…' : 'Sync Now'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        {statSevs.map(s => (
          <StatBox key={s} label={SEV_LABEL[s]} value={summary?.[s] ?? 0} color={SEV_COLOR[s]}
            active={filterSev === s}
            onClick={() => setFilterSev(prev => prev === s ? 'all' : s)}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Filter Sidebar */}
        <Paper sx={{ width: 200, flexShrink: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Severity</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, marginBottom: 2.5 }}>
            {['all', 'critical', 'high', 'medium', 'low'].map(s => (
              <FilterChip key={s} label={s === 'all' ? 'All' : SEV_LABEL[s]} active={filterSev === s} color={SEV_COLOR[s]} onClick={() => setFilterSev(s)} />
            ))}
          </Box>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Status</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {[['all', 'All', undefined], ['open', 'Open', '#f44336'], ['patched', 'Patched', '#4caf50']].map(([val, lbl, col]) => (
              <FilterChip key={val} label={lbl} active={filterPatched === val} color={col} onClick={() => setFilterPatched(val)} />
            ))}
          </Box>
        </Paper>

        {/* Vuln Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 110px 80px 80px 32px', padding: '8px 14px', marginBottom: 0.5 }}>
            <ColFilter label="Plugin / CVE" options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Title / Host"  options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Severity" options={['critical', 'high', 'medium', 'low']} value={filterSevCol} onChange={setFilterSevCol} />
            <ColFilter label="CVSS"     options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Status"   options={[]} value={[]} onChange={() => {}} />
            <Box />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
              <CircularProgress size={32} sx={{ color: '#00bcd4' }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 14 }}>
                {vulns.length === 0
                  ? 'No vulnerabilities found. Import a scan file or connect Tenable to pull data.'
                  : 'No vulnerabilities match the current filters.'}
              </Typography>
              {vulns.length === 0 && (
                <Button onClick={() => setImportOpen(true)} variant="outlined" size="small" startIcon={<UploadFileIcon />}
                  sx={{ marginTop: 2, borderColor: '#00bcd4', color: '#00bcd4', fontSize: 12 }}>
                  Import Scan
                </Button>
              )}
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filtered.map(v => <VulnRow key={v.id} v={v} onPatch={handlePatch} />)}
            </Box>
          )}

          <Typography sx={{ color: '#4a5a70', fontSize: 12, marginTop: 1.5 }}>
            Showing {filtered.length} of {vulns.filter(v =>
              filterPatched === 'all' ? true : filterPatched === 'open' ? !v.patched : v.patched
            ).length} findings
          </Typography>
        </Box>
      </Box>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadData} />
    </Box>
  );
}
